import { useState, useEffect } from 'react';
import { docSections, endpoints } from '@/mocks/docs';

const quickStartSubs = [
  { id: 'quickstart', label: 'Get started' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'errors', label: 'Errors & limits' },
];

interface DocsSidebarProps {
  open: boolean;
  onClose: () => void;
}

export default function DocsSidebar({ open, onClose }: DocsSidebarProps) {
  const [active, setActive] = useState('quickstart');

  useEffect(() => {
    const onScroll = () => {
      const sections = [...quickStartSubs.map((s) => s.id), ...endpoints.map((e) => e.id)];
      let current = 'quickstart';
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 120) {
          current = id;
        }
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const linkClasses = (id: string) =>
    `block text-sm whitespace-nowrap rounded-md px-3 py-2 transition-colors ${
      active === id ? 'bg-primary-100 text-primary-700 font-semibold' : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
    }`;

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-40 bg-foreground-950/40 lg:hidden" onClick={onClose} aria-hidden="true"></div>
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-background-50 border-r border-background-200 flex flex-col transform transition-transform duration-300 lg:sticky lg:top-16 lg:inset-y-auto lg:h-[calc(100vh-4rem)] lg:translate-x-0 lg:z-auto ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex-1 overflow-y-auto px-3 py-5 flex flex-col gap-1">
          <span className="px-3 pb-2 text-xs font-semibold uppercase tracking-wider text-foreground-400">Guide</span>
          {quickStartSubs.map((section) => (
            <a key={section.id} href={`#${section.id}`} onClick={onClose} className={linkClasses(section.id)}>
              {section.label}
            </a>
          ))}

          <span className="px-3 pb-2 pt-4 text-xs font-semibold uppercase tracking-wider text-foreground-400">
            Endpoints
          </span>
          {endpoints.map((endpoint) => (
            <a
              key={endpoint.id}
              href={`#${endpoint.id}`}
              onClick={onClose}
              className={`flex items-center gap-2 ${linkClasses(endpoint.id)}`}
            >
              <span
                className={`text-[10px] font-bold uppercase w-9 text-center rounded px-1 py-0.5 ${
                  endpoint.method === 'POST' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-800'
                }`}
              >
                {endpoint.method}
              </span>
              <span className="font-mono text-xs">{endpoint.path.replace('/loyalty-', '')}</span>
            </a>
          ))}
        </div>
      </aside>
    </>
  );
}